import {
  AccessMode,
  FanGroupBillingMode,
  LoginTypeEnum,
  PaymentMethod,
  RegistrationTypeEnum,
} from '../../enums';
import { FanGroupStatus } from '../../enums';
import { z } from 'zod';
import { TranslationValidatorSchema } from './translation.validator';
import { getTranslationValidationOptional } from '../../helpers';

export const FanGroupValidatorSchema = z.object({
  id: z.string().optional(),
  /** Set when the fan group is created from inside company settings. */
  companyId: z.string().optional().nullable(),
  name: z.array(TranslationValidatorSchema).optional(),
  shortName: z.array(TranslationValidatorSchema).optional(),
  slug: z.string(),
  companyName: z.string().optional(),
  status: z.enum(FanGroupStatus).optional(),
  termsAndConditions: z.array(TranslationValidatorSchema).optional(),
  privacyPolicy: z.array(TranslationValidatorSchema).optional(),
  operationParameters: z
    .object({
      optInPlatformTexts: z.array(TranslationValidatorSchema).optional(),
      optInPartnersTexts: z.array(TranslationValidatorSchema).optional(),
      optInTermsTexts: z.array(TranslationValidatorSchema).optional(),
      optInRulesTexts: z.array(TranslationValidatorSchema).optional(),
    })
    .optional(),
  optInPlatformTexts: z.array(TranslationValidatorSchema).optional(),
  optInPartnersTexts: z.array(TranslationValidatorSchema).optional(),
  optInTermsTexts: z.array(TranslationValidatorSchema).optional(),
  optInRulesTexts: z.array(TranslationValidatorSchema).optional(),
  geoFilteringNotificationTitle: z.array(TranslationValidatorSchema).optional(),
  geoFilteringNotificationDescription: z
    .array(TranslationValidatorSchema)
    .optional(),
  description: z.array(TranslationValidatorSchema).optional(),
  welcomeMessage: z.array(TranslationValidatorSchema).optional(),
  showLogo: z.boolean().optional(),
  showNameInHeader: z.boolean().optional(),
  rankAndLikelihoodHidden: z.boolean().optional(),
  optInPlatformHidden: z.boolean().optional(),
  optInPartnersHidden: z.boolean().optional(),
  profileImageUrl: z.any().optional(),
  color1: z.string().optional(),
  logoImageUrl: z.any().optional(),
  coverImageUrl: z.any().optional(),
  backgroundImageUrl: z.any().optional(),
  useFgColorInBanner: z.boolean().optional(),
  properties: z.record(z.string().min(1), z.unknown()).optional().nullable(),
});

export type FanGroupValidator = z.infer<typeof FanGroupValidatorSchema>;

export const FanGroupGeneralValidatorSchema = z.object({
  name: z.array(TranslationValidatorSchema),
  shortName: z.array(TranslationValidatorSchema).optional(),
  slug: z.string(),
  companyName: z.string().optional(),
  status: z.enum(FanGroupStatus).optional(),
  termsAndConditions: z.array(TranslationValidatorSchema).optional(),
  privacyPolicy: z.array(TranslationValidatorSchema).optional(),
  rulesLinks: z.array(TranslationValidatorSchema).optional(),
  legalNotice: z.array(TranslationValidatorSchema).optional(),
  faqLinks: z.array(TranslationValidatorSchema).optional(),
  helpLinks: z.array(TranslationValidatorSchema).optional(),
  optInPlatformTexts: z.array(TranslationValidatorSchema).optional(),
  optInPartnersTexts: z.array(TranslationValidatorSchema).optional(),
  optInPlatformHidden: z.boolean().optional(),
  optInPartnersHidden: z.boolean().optional(),
  optInTermsTexts: z.array(TranslationValidatorSchema).optional(),
  optInRulesTexts: z.array(TranslationValidatorSchema).optional(),
  rankAndLikelihoodHidden: z.boolean(),
  geoFilteringNotificationTitle: z.array(TranslationValidatorSchema).optional(),
  geoFilteringNotificationDescription: z
    .array(TranslationValidatorSchema)
    .optional(),
  description: z.array(TranslationValidatorSchema).optional(),
  welcomeText: z.array(TranslationValidatorSchema).optional(),
  showLogo: z.boolean(),
  showNameInHeader: z.boolean().optional(),
  highChanceBadgeEnabled: z.boolean().optional(),
  highChanceBadgeThreshold: z.number().int().min(1).nullable().optional(),
  supportEmail: z
    .union([z.string().email(), z.literal(''), z.null()])
    .optional(),
  defaultLocale: z.string().optional(),
  ticketingSystemId: z.string().optional().nullable(),
  billingParameters: z
    .object({
      authorizedFanBillingModes: z
        .array(z.enum(FanGroupBillingMode))
        .optional(),
      billingVariables: z.object({
        distributionRate: z.string().optional(),
        maxDistributionFee: z.string().optional(),
        minDistributionFee: z.string().optional(),
        vatRate: z.string().nullable().optional(),
      }),
      paymentMethods: z.array(z.enum(PaymentMethod)).optional(),
      paymentSystemId: z.string().optional(),
      prepaidFee: z.boolean().optional(),
    })
    .optional(),
  color1: z
    .string()
    .or(z.object({ hex: z.string() }))
    .optional(),
});

export type FanGroupGeneralValidator = z.infer<
  typeof FanGroupGeneralValidatorSchema
>;

export const FGRegisterConfigValidatorSchema = z.object({
  login: z.array(z.enum(LoginTypeEnum)),
  signup: z.array(z.enum(RegistrationTypeEnum)),
});

export type FGRegisterConfigValidator = z.infer<
  typeof FGRegisterConfigValidatorSchema
>;

// Base object kept unrefined (no .superRefine()) so schemas that extend it
// with additional fields (e.g. SupportStepValidatorSchema) can still call
// `.extend()`, which is only available on ZodObject and not on the
// ZodEffects produced by `.superRefine()`.
export const AuthConfigBaseValidatorSchema = z.object({
  login: z.object({
    basic: z.array(z.enum(LoginTypeEnum)),
    oauth: z
      .object({
        providerId: z.string().min(1, 'oauth_provider_id_required'),
        providerName: z.object({
          en: z.string().min(1, 'oauth_provider_name_required'),
          nl: z.string().optional(),
          fr: z.string().optional(),
          de: z.string().optional(),
        }),
      })
      .optional(),
  }),
  signup: z.array(z.enum(RegistrationTypeEnum)),
});

// Shared guard: at least one login method must remain enabled, otherwise
// every fan is permanently locked out with no way to sign back in through
// the UI. Reused by every schema that composes AuthConfigBaseValidatorSchema.
export const requireAtLeastOneLoginMethod = (
  data: { login: { basic: LoginTypeEnum[]; oauth?: unknown } },
  ctx: z.RefinementCtx
) => {
  if (data.login.basic.length === 0 && !data.login.oauth) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['login', 'basic'],
      message: 'validation_login_method_required',
    });
  }
};

export const AuthConfigValidatorSchema =
  AuthConfigBaseValidatorSchema.superRefine(requireAtLeastOneLoginMethod);

export type AuthConfigValidator = z.infer<typeof AuthConfigValidatorSchema>;

export const FanGroupGeneralFormValidatorSchema = z
  .object({
    name: z.record(z.string().min(1), z.string()).optional(),
    shortName: z.record(z.string().min(1), z.string()).optional(),
    slug: z.string().min(1, 'validation_url_slug_required'),
    clientId: z.string().optional(),
    companyName: z.string().optional(),
    ticketingSystemId: z.string().optional().nullable(),

    billingParameters: z
      .object({
        authorizedFanBillingModes: z.array(z.enum(FanGroupBillingMode)),
        /**
         * Required whenever `billingParameters` is present, mirroring
         * FanGroupGeneralValidatorSchema — the API rejects the parameters
         * object without it. The individual rates stay optional, so an empty
         * object satisfies both and a draft can still be saved.
         */
        billingVariables: z.object({
          distributionRate: z.string().optional(),
          maxDistributionFee: z.string().optional(),
          minDistributionFee: z.string().optional(),
          vatRate: z.string().nullable().optional(),
        }),
        paymentMethods: z.array(z.enum(PaymentMethod)),
        paymentSystemId: z.string().optional(),
        prepaidFee: z.boolean().optional(),
      })
      .optional(),
    status: z.enum(FanGroupStatus).optional(),
    termsAndConditions: z.record(z.string().min(1), z.string()).optional(),
    privacyPolicy: z.record(z.string().min(1), z.string()).optional(),
    rulesLinks: z.record(z.string().min(1), z.string()).optional(),
    legalNotice: z.record(z.string().min(1), z.string()).optional(),
    faqLinks: z.record(z.string().min(1), z.string()).optional(),
    helpLinks: z.record(z.string().min(1), z.string()).optional(),
    optInPlatformTexts: z.record(z.string().min(1), z.string()).optional(),
    optInPartnersTexts: z.record(z.string().min(1), z.string()).optional(),
    optInPlatformHidden: z.boolean().optional(),
    optInPartnersHidden: z.boolean().optional(),
    optInTermsTexts: z.record(z.string().min(1), z.string()).optional(),
    optInRulesTexts: z.record(z.string().min(1), z.string()).optional(),
    rankAndLikelihoodHidden: z.boolean(),
    // geoFilteringNotificationTitle: z
    //   .record(z.string().min(1), z.string())
    //   .optional()
    //   .nullable(),
    // geoFilteringNotificationDescription: z
    //   .record(z.string().min(1), z.string())
    //   .optional()
    //   .nullable(),
    description: z.record(z.string().min(1), z.string()).optional(),
    welcomeText: z.record(z.string().min(1), z.string()).optional(),
    showLogo: z.boolean(),
    showNameInHeader: z.boolean().optional(),
    supportEmail: z
      .union([z.string().email(), z.literal(''), z.null()])
      .optional(),
    defaultLocale: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Payment system is required for new audience creation
    if (
      !data.billingParameters?.paymentSystemId ||
      (typeof data.billingParameters.paymentSystemId === 'string' &&
        data.billingParameters.paymentSystemId.trim() === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['billingParameters', 'paymentSystemId'],
        message: 'validation_payment_system_required',
      });
    }
  });

export type FanGroupGeneralFormValidator = z.infer<
  typeof FanGroupGeneralFormValidatorSchema
>;

export const FanGroupAccessValidatorSchema = z.object({
  visibility: z.boolean(),
  accessMode: z.enum(AccessMode),
  protectionCodeExplanation: z.array(TranslationValidatorSchema),
});

export type FanGroupAccessValidator = z.infer<
  typeof FanGroupAccessValidatorSchema
>;

export const FanGroupAccessFormValidatorSchema = z
  .object({
    visibility: z.boolean(),
    accessMode: z.enum(AccessMode),
    protectionCodeExplanation: getTranslationValidationOptional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.accessMode === AccessMode.CODE_PROTECTED &&
      !data.protectionCodeExplanation.en
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['protectionCodeExplanation.en'],
        message: 'Required for code protected fan group',
      });
    }
  });
